import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { adminAPI, settingsAPI } from '@/api/client';
import { toast } from 'sonner';
import { Mail, Send, Loader2, Calendar, Filter, Shield, ShieldOff } from 'lucide-react';
import IPBlacklistManager from './IPBlacklistManager';

const SystemTools = () => {
  const [syncingSpeaking, setSyncingSpeaking] = useState(false);
  const [fixingPartial, setFixingPartial] = useState(false);
  const [results, setResults] = useState(null);
  const [debugMockId, setDebugMockId] = useState('');
  const [debugging, setDebugging] = useState(false);
  const [debugResults, setDebugResults] = useState(null);
  const [auditMockId, setAuditMockId] = useState('');
  const [auditing, setAuditing] = useState(false);
  const [auditResults, setAuditResults] = useState(null);
  const [fixingIndex, setFixingIndex] = useState(false);
  
  // Dev Tools Blocker State - fetch from API
  const [devToolsBlocked, setDevToolsBlocked] = useState(false);
  const [loadingDevTools, setLoadingDevTools] = useState(true);
  
  // Dry Run states
  const [syncDryRun, setSyncDryRun] = useState(true); // Default to dry run for safety
  const [restoreDryRun, setRestoreDryRun] = useState(true); // Default to dry run for safety
  
  // New Exam Notification States
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [pendingExams, setPendingExams] = useState(null);
  const [notificationResults, setNotificationResults] = useState(null);
  const [loadingPending, setLoadingPending] = useState(false);
  const [createdAfterDate, setCreatedAfterDate] = useState(''); // Date filter
  const [useCreatedAfterFilter, setUseCreatedAfterFilter] = useState(false);
  
  // Fetch dev tools setting from API on mount
  useEffect(() => {
    const fetchDevToolsSetting = async () => {
      try {
        const response = await settingsAPI.getSiteSettings();
        setDevToolsBlocked(response.data.dev_tools_blocked === true);
      } catch (error) {
        console.error('Failed to fetch dev tools setting:', error);
      } finally {
        setLoadingDevTools(false);
      }
    };
    fetchDevToolsSetting();
  }, []);
  
  // Handle Dev Tools Toggle - saves to DATABASE via API
  const handleDevToolsToggle = async (checked) => {
    try {
      setDevToolsBlocked(checked);
      
      // Save to database via API
      const response = await settingsAPI.updateDevToolsSetting(checked);
      
      if (response.data.success) {
        // Clear local cache so script fetches fresh
        localStorage.removeItem('devToolsBlocked_cache');
        localStorage.removeItem('devToolsBlocked_cache_time');
        
        // Tell the blocker script to refresh
        if (window.DevToolsBlocker && window.DevToolsBlocker.refresh) {
          window.DevToolsBlocker.refresh();
        }
        
        if (checked) {
          toast.success('🔒 Dev Tools BLOCKED - সব browser ও user এর জন্য কার্যকর হবে');
        } else {
          toast.info('🔓 Dev Tools ALLOWED - সব developer tools enabled');
        }
      }
    } catch (error) {
      console.error('Failed to update dev tools setting:', error);
      toast.error('সেটিং আপডেট করতে সমস্যা হয়েছে');
      // Revert state on error
      setDevToolsBlocked(!checked);
    }
  };
  
  // Fetch pending exam notifications on mount and when filter changes
  useEffect(() => {
    fetchPendingNotifications();
  }, []);
  
  const fetchPendingNotifications = async (filterDate = null) => {
    setLoadingPending(true);
    try {
      const dateToUse = filterDate !== null ? filterDate : (useCreatedAfterFilter ? createdAfterDate : null);
      const response = await adminAPI.getPendingExamNotifications(dateToUse);
      setPendingExams(response.data);
    } catch (error) {
      console.error('Failed to fetch pending notifications:', error);
    } finally {
      setLoadingPending(false);
    }
  };
  
  const handleApplyDateFilter = () => {
    if (useCreatedAfterFilter && !createdAfterDate) {
      toast.error('অনুগ্রহ করে একটি date select করুন');
      return;
    }
    fetchPendingNotifications(useCreatedAfterFilter ? createdAfterDate : null);
  };
  
  const handleSendExamNotifications = async () => {
    if (!pendingExams?.has_pending) {
      toast.info('কোন নতুন exam নেই notification পাঠানোর জন্য');
      return;
    }
    
    const totalCD = pendingExams.pending_exams.total_cd || 0;
    const totalPB = pendingExams.pending_exams.total_pb || 0;
    const cdStudents = pendingExams.students.cd_students || 0;
    const pbStudents = pendingExams.students.pb_students || 0;
    
    // Build confirmation message showing only categories with exams
    let studentInfo = '';
    if (totalCD > 0) {
      studentInfo += `CD Students: ${cdStudents} জন (${totalCD}টি exam সম্পর্কে)\n`;
    }
    if (totalPB > 0) {
      studentInfo += `PB Students: ${pbStudents} জন (${totalPB}টি exam সম্পর্কে)\n`;
    }
    
    // Show which categories will be skipped
    let skipInfo = '';
    if (totalCD === 0 && cdStudents > 0) {
      skipInfo += `\n⏭️ CD Students (${cdStudents} জন) skip হবে - কোন CD exam নেই\n`;
    }
    if (totalPB === 0 && pbStudents > 0) {
      skipInfo += `\n⏭️ PB Students (${pbStudents} জন) skip হবে - কোন PB exam নেই\n`;
    }
    
    let filterInfo = '';
    if (useCreatedAfterFilter && createdAfterDate) {
      filterInfo = `\n📅 Filter: শুধু ${createdAfterDate} এর পরে তৈরি exams\n`;
    }
    
    const confirmMsg = `এটা নিম্নলিখিত students দের email পাঠাবে:\n\n` +
      studentInfo +
      skipInfo +
      filterInfo +
      `\n⏱️ প্রতিটি email এর মধ্যে 2 সেকেন্ড gap থাকবে।\n\n` +
      `Continue করতে চান?`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    setSendingNotifications(true);
    setNotificationResults(null);
    
    try {
      const dateToUse = useCreatedAfterFilter ? createdAfterDate : null;
      const response = await adminAPI.sendNewExamNotifications(dateToUse);
      setNotificationResults(response.data);
      
      const totalSent = (response.data.cd_students_notified || 0) + (response.data.pb_students_notified || 0);
      const failed = response.data.failed_emails || 0;
      
      if (failed > 0) {
        toast.warning(`✅ ${totalSent} জনকে email পাঠানো হয়েছে, ${failed}টি failed (Email Queue তে আছে)`);
      } else if (totalSent > 0) {
        toast.success(`✅ ${totalSent} জন student কে সফলভাবে email পাঠানো হয়েছে!`);
      } else {
        toast.info('কোন email পাঠানো হয়নি');
      }
      
      // Refresh pending count
      fetchPendingNotifications();
      
    } catch (error) {
      console.error('Send notifications failed:', error);
      toast.error(error.response?.data?.detail || 'Notification পাঠাতে সমস্যা হয়েছে');
    } finally {
      setSendingNotifications(false);
    }
  };

  // Fix User Mock Index (for Other Student restore issues)
  const handleFixUserMockIndex = async () => {
    if (!window.confirm('এটা unique_user_mock_booked index ঠিক করবে যাতে Other Student bookings restore করা যায়। Continue করতে চান?')) {
      return;
    }
    
    setFixingIndex(true);
    
    try {
      const response = await adminAPI.fixUniqueUserMockIndex();
      setResults({
        type: 'index_fix',
        ...response.data
      });
      
      if (response.data.status === 'success') {
        toast.success(`✅ Index fix সম্পন্ন! ${response.data.other_students_fixed} bookings ঠিক করা হয়েছে।`);
      }
    } catch (error) {
      console.error('Fix index failed:', error);
      toast.error(error.response?.data?.detail || 'Fix failed');
      setResults({
        type: 'index_fix',
        status: 'error',
        error: error.response?.data?.detail || error.message
      });
    } finally {
      setFixingIndex(false);
    }
  };
  
  // Convert Exam created_at to BD Time
  const [convertingTimezone, setConvertingTimezone] = useState(false);
  const [timezoneResults, setTimezoneResults] = useState(null);
  
  const handleConvertCreatedAtToBdTime = async (dryRun = true) => {
    const actionText = dryRun 
      ? 'DRY RUN: এটা দেখাবে কোন exams এর created_at পরিবর্তন হবে (কিছু change হবে না)।'
      : '⚠️ এটা সত্যিই সব exams এর created_at UTC থেকে BD Time (+6 ঘন্টা) এ convert করবে!';
    
    if (!window.confirm(`${actionText}\n\nContinue করতে চান?`)) {
      return;
    }
    
    setConvertingTimezone(true);
    setTimezoneResults(null);
    
    try {
      const response = await adminAPI.convertExamCreatedAtToBdTime(dryRun);
      setTimezoneResults(response.data);
      
      if (dryRun) {
        toast.info(`DRY RUN: ${response.data.partial_exams_updated} partial + ${response.data.mock_exams_updated} mock exams convert হবে`);
      } else {
        toast.success(`✅ সফল! ${response.data.partial_exams_updated} partial + ${response.data.mock_exams_updated} mock exams BD Time এ convert হয়েছে`);
      }
    } catch (error) {
      console.error('Timezone conversion failed:', error);
      toast.error(error.response?.data?.detail || 'Conversion failed');
    } finally {
      setConvertingTimezone(false);
    }
  };

  // Sync Mock Speaking Slots
  const handleSyncSpeakingSlots = async () => {
    const actionText = syncDryRun 
      ? 'এটা দেখাবে কোন slots পরিবর্তন হবে (কিছু change হবে না)।'
      : '⚠️ সতর্কতা! এটা সত্যিই ডাটাবেস পরিবর্তন করবে!';
    
    if (!window.confirm(`${actionText}\n\nContinue করতে চান?`)) {
      return;
    }
    
    setSyncingSpeaking(true);
    setResults(null);
    
    try {
      const response = await adminAPI.syncMockSpeakingSlots(syncDryRun);
      const data = response.data;
      
      setResults({
        type: 'speaking',
        ...data
      });
      
      if (data.dry_run) {
        toast.info(`🔍 DRY RUN: ${data.changes_preview?.length || 0}টি slot পরিবর্তন হবে`);
      } else if (data.status === 'success') {
        const totalFixed = (data.slots_set_to_booked || 0) + (data.slots_set_to_available || 0);
        toast.success(`✅ ${totalFixed}টি slot সংশোধন করা হয়েছে!`);
      } else {
        toast.error('কিছু সমস্যা হয়েছে');
      }
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error(error.response?.data?.detail || 'Sync failed');
      setResults({
        type: 'speaking',
        status: 'error',
        error: error.response?.data?.detail || error.message
      });
    } finally {
      setSyncingSpeaking(false);
    }
  };

  // Restore Cancelled Bookings
  const handleRestoreBookings = async () => {
    if (!auditMockId.trim()) {
      toast.error('আগে Mock ID দিন!');
      return;
    }
    
    const actionText = restoreDryRun 
      ? 'এটা দেখাবে কোন bookings restore হবে (কিছু change হবে না)।'
      : `⚠️ সতর্কতা!\n\nএটা ${auditMockId} এর সব CANCELLED bookings সত্যিই restore করবে।`;
    
    if (!window.confirm(`${actionText}\n\nContinue করতে চান?`)) {
      return;
    }
    
    setAuditing(true);
    
    try {
      const response = await adminAPI.restoreCancelledMockBookings(auditMockId.trim(), restoreDryRun);
      const data = response.data;
      
      setAuditResults(prev => ({
        ...prev,
        recovery_result: data
      }));
      
      if (data.dry_run) {
        toast.info(`🔍 DRY RUN: ${data.bookings_restored || 0}টি booking restore হবে`);
      } else if (data.bookings_restored > 0) {
        toast.success(`✅ ${data.bookings_restored}টি booking restore হয়েছে!`);
        // Re-run audit to show updated data
        handleFullAudit();
      } else if (data.skipped_bookings?.length > 0) {
        toast.warning(`⚠️ ${data.skipped_bookings.length}টি booking skip হয়েছে। নিচে কারণ দেখুন।`);
      } else if (data.found_cancelled === 0) {
        toast.info('কোন cancelled booking পাওয়া যায়নি');
      } else {
        toast.info('কোন booking restore করা হয়নি');
      }
    } catch (error) {
      console.error('Restore failed:', error);
      toast.error(error.response?.data?.detail || 'Restore failed');
    } finally {
      setAuditing(false);
    }
  };

  // Diagnose Restore Issues
  const handleDiagnoseRestore = async () => {
    if (!auditMockId.trim()) {
      toast.error('আগে Mock ID দিন!');
      return;
    }
    
    setAuditing(true);
    
    try {
      const response = await adminAPI.diagnoseRestoreIssue(auditMockId.trim());
      setAuditResults(prev => ({
        ...prev,
        diagnosis_result: response.data
      }));
      toast.info('Diagnosis সম্পন্ন! নিচে details দেখুন।');
    } catch (error) {
      console.error('Diagnose failed:', error);
      toast.error(error.response?.data?.detail || 'Diagnose failed');
    } finally {
      setAuditing(false);
    }
  };

  // Debug Specific Mock Speaking Slots
  const handleDebugSpecificMock = async () => {
    if (!auditMockId.trim()) {
      toast.error('আগে Mock ID দিন!');
      return;
    }
    
    setAuditing(true);
    
    try {
      const response = await adminAPI.debugSpecificMock(auditMockId.trim());
      setAuditResults(prev => ({
        ...prev,
        debug_result: response.data
      }));
      
      if (response.data.issues?.length > 0) {
        toast.warning(`⚠️ ${response.data.issues.length}টি সমস্যা পাওয়া গেছে!`);
      } else {
        toast.success('কোন সমস্যা নেই!');
      }
    } catch (error) {
      console.error('Debug failed:', error);
      toast.error(error.response?.data?.detail || 'Debug failed');
    } finally {
      setAuditing(false);
    }
  };

  // Full Mock Audit
  const handleFullAudit = async () => {
    if (!auditMockId.trim()) {
      toast.error('Mock ID দিন (যেমন: MK2025DEC13F)');
      return;
    }
    
    setAuditing(true);
    setAuditResults(null);
    
    try {
      const response = await adminAPI.fullMockAudit(auditMockId.trim());
      setAuditResults(response.data);
      
      if (response.data.summary?.issues_detected?.length > 0) {
        toast.warning('সমস্যা পাওয়া গেছে! নিচে details দেখুন।');
      } else {
        toast.success('অডিট সম্পন্ন!');
      }
    } catch (error) {
      console.error('Audit failed:', error);
      toast.error(error.response?.data?.detail || 'Audit failed');
    } finally {
      setAuditing(false);
    }
  };

  // Debug Mock Speaking
  const handleDebugMock = async () => {
    if (!debugMockId.trim()) {
      toast.error('Mock ID দিন (যেমন: MK2025DEC13F)');
      return;
    }
    
    setDebugging(true);
    setDebugResults(null);
    
    try {
      const response = await adminAPI.debugMockSpeaking(debugMockId.trim());
      setDebugResults(response.data);
      
      if (response.data.analysis?.mismatch_detected) {
        toast.warning('Mismatch পাওয়া গেছে! নিচে details দেখুন।');
      } else {
        toast.success('সব ঠিক আছে!');
      }
    } catch (error) {
      console.error('Debug failed:', error);
      toast.error(error.response?.data?.detail || 'Debug failed');
    } finally {
      setDebugging(false);
    }
  };

  // Fix Partial Booking Duplicates
  const handleFixPartialBooking = async () => {
    if (!window.confirm('এটা Partial booking duplicate সমস্যা ঠিক করবে এবং unique index তৈরি করবে। Continue করতে চান?')) {
      return;
    }
    
    setFixingPartial(true);
    setResults(null);
    
    try {
      const response = await adminAPI.fixPartialBookingIndex();
      const data = response.data;
      
      setResults({
        type: 'partial',
        ...data
      });
      
      if (data.status === 'success' || data.status === 'partial_success') {
        toast.success(`✅ ${data.duplicates_cancelled || 0} duplicate বাতিল করা হয়েছে!`);
      } else {
        toast.error('কিছু সমস্যা হয়েছে');
      }
    } catch (error) {
      console.error('Fix failed:', error);
      toast.error(error.response?.data?.detail || 'Fix failed');
      setResults({
        type: 'partial',
        status: 'error',
        error: error.response?.data?.detail || error.message
      });
    } finally {
      setFixingPartial(false);
    }
  };

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            🛠️ System Tools
          </CardTitle>
          <CardDescription>
            Database সংশোধন এবং maintenance tools। শুধুমাত্র প্রয়োজনে ব্যবহার করুন।
          </CardDescription>
        </CardHeader>
      </Card>
      
      {/* Dev Tools Blocker Card */}
      <Card className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-indigo-50">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            {devToolsBlocked ? (
              <Shield className="h-5 w-5 text-green-600" />
            ) : (
              <ShieldOff className="h-5 w-5 text-red-500" />
            )}
            🔒 Dev Tools Protection (Global)
            {loadingDevTools ? (
              <Badge className="bg-gray-400 text-white">Loading...</Badge>
            ) : (
              <Badge className={devToolsBlocked ? "bg-green-500 text-white" : "bg-red-500 text-white"}>
                {devToolsBlocked ? "PROTECTED" : "UNPROTECTED"}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            সব browser ও user এর জন্য developer tools (Inspect, Ctrl+U, Right-click) block/unblock করুন।
            <span className="text-purple-600 font-medium"> Database এ সংরক্ষিত হয়।</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
            <div className="space-y-1">
              <p className="font-medium text-gray-900">
                {devToolsBlocked ? "🛡️ Dev Tools Blocked" : "⚠️ Dev Tools Allowed"}
              </p>
              <p className="text-sm text-gray-500">
                {devToolsBlocked 
                  ? "Right-click, F12, Ctrl+U, Ctrl+Shift+I সব disabled আছে" 
                  : "সব developer tools enabled আছে - Production এ OFF রাখুন"
                }
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${devToolsBlocked ? 'text-green-600' : 'text-gray-400'}`}>
                {devToolsBlocked ? 'ON' : 'OFF'}
              </span>
              <Switch
                checked={devToolsBlocked}
                onCheckedChange={handleDevToolsToggle}
                disabled={loadingDevTools}
                className="data-[state=checked]:bg-green-600"
              />
            </div>
          </div>
          <p className="text-xs text-green-600 mt-3 font-medium">
            ✨ এই সেটিং সব browser ও user এর জন্য automatically কাজ করবে
          </p>
        </CardContent>
      </Card>
      
      {/* NEW: Email Notification Card - Full Width */}
      <Card className="border-2 border-green-300 bg-gradient-to-br from-green-50 to-emerald-50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="h-5 w-5 text-green-600" />
            📧 New Exam Slot Notification
            {pendingExams?.has_pending && (
              <Badge className="bg-green-500 text-white animate-pulse">
                New Exams Available!
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            নতুন Exam slot তৈরি করার পর students দের email notification পাঠান।
            Category অনুযায়ী CD students কে CD exams এবং PB students কে PB exams এর notification যাবে।
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Pending Exams Summary */}
          {loadingPending ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading pending exams...
            </div>
          ) : pendingExams ? (
            <div className="grid grid-cols-2 gap-4">
              {/* CD Exams */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">CD (Computer Delivered)</h4>
                <div className="space-y-1 text-sm">
                  <p className="text-gray-700">
                    Partial Exams: <strong className="text-blue-600">{pendingExams.pending_exams.cd_partial || 0}</strong>
                  </p>
                  <p className="text-gray-700">
                    Speaking Exams: <strong className="text-purple-600">{pendingExams.pending_exams.shared_speaking || 0}</strong>
                    <span className="text-xs text-gray-500 ml-1">(shared)</span>
                  </p>
                  <p className="text-gray-700">
                    Mock Tests: <strong className="text-blue-600">{pendingExams.pending_exams.cd_mock || 0}</strong>
                  </p>
                  <hr className="my-2" />
                  <p className="text-gray-700">
                    Students: <strong className="text-blue-700">{pendingExams.students.cd_students || 0}</strong> জন
                  </p>
                </div>
              </div>
              
              {/* PB Exams */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-800 mb-2">PB (Paper Based)</h4>
                <div className="space-y-1 text-sm">
                  <p className="text-gray-700">
                    Partial Exams: <strong className="text-orange-600">{pendingExams.pending_exams.pb_partial || 0}</strong>
                  </p>
                  <p className="text-gray-700">
                    Speaking Exams: <strong className="text-purple-600">{pendingExams.pending_exams.shared_speaking || 0}</strong>
                    <span className="text-xs text-gray-500 ml-1">(shared)</span>
                  </p>
                  <p className="text-gray-700">
                    Mock Tests: <strong className="text-orange-600">{pendingExams.pending_exams.pb_mock || 0}</strong>
                  </p>
                  <hr className="my-2" />
                  <p className="text-gray-700">
                    Students: <strong className="text-orange-700">{pendingExams.students.pb_students || 0}</strong> জন
                  </p>
                </div>
              </div>
            </div>
          ) : null}
          
          {/* Date Filter Section */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="h-4 w-4 text-purple-600" />
              <Label className="font-semibold text-purple-800">📅 Date Filter (Optional)</Label>
            </div>
            <p className="text-sm text-purple-700 mb-3">
              পুরানো exams বাদ দিতে চাইলে, একটি date select করুন। শুধু ওই date এর পরে তৈরি exams দেখাবে এবং send হবে।
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="useCreatedAfterFilter" 
                  checked={useCreatedAfterFilter}
                  onCheckedChange={(checked) => {
                    setUseCreatedAfterFilter(checked);
                    if (!checked) {
                      setCreatedAfterDate('');
                      fetchPendingNotifications(null);
                    }
                  }}
                />
                <label htmlFor="useCreatedAfterFilter" className="text-sm cursor-pointer text-gray-700">
                  Date filter ব্যবহার করুন
                </label>
              </div>
              
              {useCreatedAfterFilter && (
                <>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-purple-600" />
                    <Input
                      type="datetime-local"
                      value={createdAfterDate}
                      onChange={(e) => setCreatedAfterDate(e.target.value)}
                      className="w-56 border-purple-300 focus:border-purple-500"
                      placeholder="Select date & time"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleApplyDateFilter}
                    disabled={loadingPending || !createdAfterDate}
                    className="border-purple-400 text-purple-700 hover:bg-purple-100"
                  >
                    Apply Filter
                  </Button>
                </>
              )}
            </div>
            {useCreatedAfterFilter && createdAfterDate && pendingExams?.created_after_filter && (
              <div className="mt-3 text-sm bg-purple-100 rounded p-2">
                <strong className="text-purple-800">Active Filter:</strong> 
                <span className="text-purple-700 ml-1">
                  {new Date(createdAfterDate).toLocaleString('en-BD', { 
                    dateStyle: 'medium', 
                    timeStyle: 'short' 
                  })} এর পরে তৈরি exams
                </span>
              </div>
            )}
          </div>
          
          {/* Info Box */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
            <p className="text-amber-800">
              <strong>⚠️ গুরুত্বপূর্ণ:</strong> প্রতিটি email এর মধ্যে 2 সেকেন্ড gap থাকবে rate limit এড়াতে।
              Failed emails Email Queue তে যাবে এবং পরে Resend করা যাবে।
            </p>
          </div>
          
          {/* Send Button */}
          <div className="flex gap-3">
            <Button 
              onClick={handleSendExamNotifications}
              disabled={sendingNotifications || !pendingExams?.has_pending}
              className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
            >
              {sendingNotifications ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending Emails...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  📨 Send New Exam Notifications
                </>
              )}
            </Button>
            <Button 
              variant="outline"
              onClick={fetchPendingNotifications}
              disabled={loadingPending}
            >
              🔄 Refresh
            </Button>
          </div>
          
          {/* Results */}
          {notificationResults && (
            <div className={`p-4 rounded-lg border-2 ${notificationResults.failed_emails > 0 ? 'bg-yellow-50 border-yellow-300' : 'bg-green-50 border-green-300'}`}>
              <h4 className="font-semibold text-gray-800 mb-3">📊 Notification Results:</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-white p-3 rounded border">
                  <p className="text-gray-600">CD Students Notified</p>
                  <p className="text-2xl font-bold text-blue-600">{notificationResults.cd_students_notified || 0}</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-gray-600">PB Students Notified</p>
                  <p className="text-2xl font-bold text-orange-600">{notificationResults.pb_students_notified || 0}</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-gray-600">Failed Emails</p>
                  <p className={`text-2xl font-bold ${notificationResults.failed_emails > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {notificationResults.failed_emails || 0}
                  </p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-gray-600">Skipped (No Email)</p>
                  <p className="text-2xl font-bold text-gray-500">{notificationResults.skipped_students || 0}</p>
                </div>
              </div>
              {notificationResults.failed_emails > 0 && (
                <p className="mt-3 text-sm text-amber-700">
                  ⚠️ Failed emails Email Queue তে আছে। Email Queue Manager থেকে Resend করুন।
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Timezone Migration Card - Full Width */}
      <Card className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-indigo-50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            🕐 Exam Created Time → BD Time Convert
          </CardTitle>
          <CardDescription>
            পুরানো exams এর <code>created_at</code> UTC থেকে BD Time (UTC+6) এ convert করুন।
            এটা Date Filter ঠিকমতো কাজ করার জন্য প্রয়োজন।
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
            <p className="text-amber-800">
              <strong>⚠️ কখন চালাবেন:</strong> যদি Date Filter এ সঠিক সময়ে তৈরি exams না দেখায়, 
              তাহলে এটা চালান। এটা সব exams এর created_at +6 ঘন্টা করবে।
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button 
              onClick={() => handleConvertCreatedAtToBdTime(true)}
              disabled={convertingTimezone}
              variant="outline"
              className="flex-1 border-purple-400 text-purple-700 hover:bg-purple-100"
            >
              {convertingTimezone ? '⏳ Checking...' : '🔍 DRY RUN (Preview)'}
            </Button>
            <Button 
              onClick={() => handleConvertCreatedAtToBdTime(false)}
              disabled={convertingTimezone}
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
            >
              {convertingTimezone ? '⏳ Converting...' : '🕐 Convert to BD Time'}
            </Button>
          </div>
          
          {timezoneResults && (
            <div className="bg-white border rounded-lg p-4">
              <p className="font-semibold text-gray-800 mb-2">
                {timezoneResults.dry_run ? '🔍 DRY RUN Result:' : '✅ Conversion Result:'}
              </p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-blue-50 p-2 rounded">
                  <span className="text-gray-600">Partial Exams:</span>
                  <span className="font-bold text-blue-600 ml-2">{timezoneResults.partial_exams_updated}</span>
                </div>
                <div className="bg-purple-50 p-2 rounded">
                  <span className="text-gray-600">Mock Exams:</span>
                  <span className="font-bold text-purple-600 ml-2">{timezoneResults.mock_exams_updated}</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">{timezoneResults.message}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tool Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        
        {/* Sync Speaking Slots */}
        <Card className="border-2 border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              🎤 Mock Speaking Slot Sync
            </CardTitle>
            <CardDescription>
              Mock exam card এ Speaking slot count ভুল দেখালে এটা চালান।
              এটা সব speaking slots এর booked/available status ঠিক করবে।
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Dry Run Checkbox */}
            <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <Checkbox 
                id="syncDryRun" 
                checked={syncDryRun}
                onCheckedChange={setSyncDryRun}
              />
              <label htmlFor="syncDryRun" className="text-sm cursor-pointer flex items-center gap-2">
                <span className="font-medium">🔍 Dry Run</span>
                <span className="text-gray-600">(শুধু দেখাবে কি হবে, কিছু change হবে না)</span>
              </label>
            </div>
            
            <Button 
              onClick={handleSyncSpeakingSlots}
              disabled={syncingSpeaking}
              className={`w-full ${syncDryRun ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {syncingSpeaking ? '⏳ Checking...' : syncDryRun ? '🔍 Preview দেখুন' : '🔄 Speaking Slots Sync করুন'}
            </Button>
          </CardContent>
        </Card>

        {/* Fix Partial Booking Index */}
        <Card className="border-2 border-orange-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              📋 Partial Booking Duplicate Fix
            </CardTitle>
            <CardDescription>
              একই student একই partial exam এ একাধিক বার book করে ফেললে এটা চালান।
              এটা duplicate বাতিল করবে এবং ভবিষ্যতে এরকম হওয়া বন্ধ করবে।
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleFixPartialBooking}
              disabled={fixingPartial}
              className="w-full bg-orange-600 hover:bg-orange-700"
            >
              {fixingPartial ? '⏳ Fixing...' : '🔧 Duplicate Fix করুন'}
            </Button>
          </CardContent>
        </Card>

        {/* Fix Index for Other Student Restore */}
        <Card className="border-2 border-purple-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              🔧 Fix Other Student Index
            </CardTitle>
            <CardDescription>
              Other Student bookings restore করতে গেলে Duplicate key error আসলে এটা চালান।
              এটা database index ঠিক করবে।
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleFixUserMockIndex}
              disabled={fixingIndex}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {fixingIndex ? '⏳ Fixing Index...' : '🔧 Index Fix করুন'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Results */}
      {results && (
        <Card className={`border-2 ${results.dry_run ? 'border-yellow-300 bg-yellow-50' : results.status === 'success' ? 'border-green-300 bg-green-50' : results.status === 'error' ? 'border-red-300 bg-red-50' : 'border-yellow-300 bg-yellow-50'}`}>
          <CardHeader>
            <CardTitle className="text-lg">
              {results.dry_run ? '🔍 DRY RUN Preview' : results.status === 'success' ? '✅ সফল!' : results.status === 'error' ? '❌ Error' : '⚠️ আংশিক সফল'}
            </CardTitle>
            {results.dry_run && (
              <CardDescription className="text-yellow-700 font-medium">
                এটা শুধু preview - কোন পরিবর্তন হয়নি। নিশ্চিত হলে Dry Run off করে আবার চালান।
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {results.type === 'speaking' && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm bg-white p-3 rounded">
                  <div>মোট Slots চেক করা: <strong>{results.slots_checked || 0}</strong></div>
                  {results.dry_run ? (
                    <>
                      <div>Booked এ পরিবর্তন হবে: <strong className="text-orange-600">{results.changes_preview?.filter(c => c.action === 'SET_BOOKED').length || 0}</strong></div>
                      <div>Available এ পরিবর্তন হবে: <strong className="text-green-600">{results.changes_preview?.filter(c => c.action === 'SET_AVAILABLE').length || 0}</strong></div>
                    </>
                  ) : (
                    <>
                      <div>Booked এ পরিবর্তন: <strong className="text-orange-600">{results.slots_set_to_booked || 0}</strong></div>
                      <div>Available এ পরিবর্তন: <strong className="text-green-600">{results.slots_set_to_available || 0}</strong></div>
                    </>
                  )}
                </div>
                
                {results.message && <p className={results.dry_run ? 'text-yellow-700' : 'text-green-700'}>{results.message}</p>}
                
                {/* Dry Run Changes Preview */}
                {results.dry_run && results.changes_preview?.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <h4 className="font-semibold text-yellow-800">📋 পরিবর্তনের তালিকা:</h4>
                    <div className="max-h-[300px] overflow-y-auto space-y-2">
                      {results.changes_preview.map((change, i) => (
                        <div key={i} className={`p-2 rounded text-sm ${change.action === 'SET_BOOKED' ? 'bg-orange-100' : 'bg-green-100'}`}>
                          <div className="flex justify-between items-center">
                            <span className="font-medium">{change.mock_uid}</span>
                            <span className={`px-2 py-0.5 rounded text-xs ${change.action === 'SET_BOOKED' ? 'bg-orange-200' : 'bg-green-200'}`}>
                              {change.action === 'SET_BOOKED' ? '→ Booked' : '→ Available'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            <div>Time: {change.slot_time}</div>
                            <div>Reason: {change.reason}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {results.dry_run && results.changes_preview?.length === 0 && (
                  <div className="p-3 bg-green-100 rounded text-green-700">
                    ✅ কোন পরিবর্তন প্রয়োজন নেই - সব ঠিক আছে!
                  </div>
                )}
              </div>
            )}
            
            {results.type === 'partial' && (
              <div className="space-y-2">
                <p><strong>Duplicate পাওয়া গেছে:</strong> {results.duplicates_found?.length || 0}</p>
                <p><strong>বাতিল করা হয়েছে:</strong> {results.duplicates_cancelled || 0}</p>
                {results.actions && results.actions.length > 0 && (
                  <div>
                    <strong>Actions:</strong>
                    <ul className="list-disc list-inside text-sm mt-1">
                      {results.actions.map((action, i) => (
                        <li key={i}>{action}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {results.errors && results.errors.length > 0 && (
                  <div className="text-red-600">
                    <strong>Errors:</strong>
                    <ul className="list-disc list-inside text-sm mt-1">
                      {results.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            {results.type === 'index_fix' && (
              <div className="space-y-2">
                <p><strong>পুরাতন Index মুছে ফেলা হয়েছে:</strong> {results.old_index_dropped ? '✅ হ্যাঁ' : '❌ না'}</p>
                <p><strong>নতুন Index তৈরি হয়েছে:</strong> {results.new_index_created ? '✅ হ্যাঁ' : '❌ না'}</p>
                <p><strong>Other Student Bookings ঠিক করা হয়েছে:</strong> {results.other_students_fixed || 0}</p>
                {results.message && <p className="text-green-700 mt-2">{results.message}</p>}
                {results.errors && results.errors.length > 0 && (
                  <div className="text-red-600 mt-2">
                    <strong>Errors:</strong>
                    <ul className="list-disc list-inside text-sm mt-1">
                      {results.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            {results.error && (
              <p className="text-red-600">{results.error}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Full Mock Audit - NEW */}
      <Card className="border-2 border-red-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            🔴 Full Mock Audit (Data Recovery)
          </CardTitle>
          <CardDescription>
            Mock এর সব booking দেখুন - active, cancelled সব। 
            Booking হারিয়ে গেলে বা merge হয়ে গেলে এটা দিয়ে খুঁজে বের করুন।
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input 
              placeholder="Mock ID (যেমন: MK2025DEC13V)"
              value={auditMockId}
              onChange={(e) => setAuditMockId(e.target.value)}
              className="flex-1"
            />
            <Button 
              onClick={handleFullAudit}
              disabled={auditing}
              className="bg-red-600 hover:bg-red-700"
            >
              {auditing ? '⏳ Checking...' : '🔍 Full Audit'}
            </Button>
            <Button 
              onClick={handleDebugSpecificMock}
              disabled={auditing || !auditMockId.trim()}
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              {auditing ? '⏳...' : '🔬 Debug Speaking'}
            </Button>
          </div>
          
          {/* Debug Speaking Result */}
          {auditResults?.debug_result && (
            <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
              <h4 className="font-semibold text-blue-800 mb-2">🔬 Speaking Slot Debug</h4>
              
              <div className="grid grid-cols-4 gap-2 text-sm bg-white p-3 rounded mb-3">
                <div>Total Slots: <strong>{auditResults.debug_result.summary?.total_slots || 0}</strong></div>
                <div>Marked Booked: <strong className="text-orange-600">{auditResults.debug_result.summary?.slots_marked_booked || 0}</strong></div>
                <div>Actual Booked: <strong className="text-green-600">{auditResults.debug_result.summary?.actual_booked_bookings || 0}</strong></div>
                <div>Orphan: <strong className="text-red-600">{auditResults.debug_result.summary?.orphan_slots || 0}</strong></div>
              </div>
              
              {auditResults.debug_result.issues?.length > 0 && (
                <div className="mb-3">
                  <h5 className="text-sm font-medium text-red-700 mb-1">⚠️ সমস্যা পাওয়া গেছে:</h5>
                  <div className="space-y-1 max-h-[150px] overflow-y-auto">
                    {auditResults.debug_result.issues.map((issue, i) => (
                      <div key={i} className="p-2 bg-red-100 rounded text-xs text-red-700">{issue}</div>
                    ))}
                  </div>
                </div>
              )}
              
              {auditResults.debug_result.orphan_slots?.length > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-orange-700 mb-1">🔴 Orphan Slots (ভুলভাবে booked দেখাচ্ছে):</h5>
                  <div className="space-y-1 max-h-[150px] overflow-y-auto">
                    {auditResults.debug_result.orphan_slots.map((slot, i) => (
                      <div key={i} className="p-2 bg-orange-100 rounded text-xs">
                        <span className="font-medium">{slot.time}</span> ({slot.date})
                        <div className="text-orange-700">{slot.status}</div>
                        <div className="text-gray-600">Booked By in DB: {slot.booked_by_in_db || 'N/A'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {auditResults.debug_result.issues?.length === 0 && (
                <div className="p-3 bg-green-100 rounded text-green-700">
                  ✅ কোন সমস্যা নেই - Speaking slots সব ঠিক আছে!
                </div>
              )}
            </div>
          )}
          
          {/* Restore Section with Dry Run */}
          <div className="p-3 bg-green-50 rounded-lg border border-green-200 space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox 
                id="restoreDryRun" 
                checked={restoreDryRun}
                onCheckedChange={setRestoreDryRun}
              />
              <label htmlFor="restoreDryRun" className="text-sm cursor-pointer flex items-center gap-2">
                <span className="font-medium">🔍 Dry Run</span>
                <span className="text-gray-600">(শুধু দেখাবে কি restore হবে)</span>
              </label>
            </div>
            <Button 
              onClick={handleRestoreBookings}
              disabled={auditing || !auditMockId.trim()}
              className={`w-full ${restoreDryRun ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'}`}
            >
              {auditing ? '⏳...' : restoreDryRun ? '🔍 Restore Preview দেখুন' : '♻️ Restore All'}
            </Button>
            <Button 
              onClick={handleDiagnoseRestore}
              disabled={auditing || !auditMockId.trim()}
              variant="outline"
              className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
            >
              {auditing ? '⏳...' : '🔬 Diagnose Restore Issue'}
            </Button>
          </div>
          
          {/* Diagnosis Result */}
          {auditResults?.diagnosis_result && (
            <div className="p-4 bg-purple-50 rounded-lg border-2 border-purple-300">
              <h4 className="font-semibold text-purple-800 mb-2">🔬 Diagnosis Result</h4>
              <p className="text-sm text-purple-700 mb-3">{auditResults.diagnosis_result.diagnosis}</p>
              
              {/* Indexes */}
              <div className="mb-3">
                <h5 className="text-sm font-medium text-gray-700 mb-1">Database Indexes:</h5>
                <div className="text-xs bg-white p-2 rounded max-h-[100px] overflow-y-auto">
                  {auditResults.diagnosis_result.indexes?.map((idx, i) => (
                    <div key={i} className="mb-1">
                      <span className="font-medium">{idx.name}</span>
                      {idx.unique && <span className="ml-1 text-red-600">(UNIQUE)</span>}
                      {idx.partialFilterExpression && (
                        <span className="ml-1 text-orange-600">(Partial)</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Cancelled Bookings with Conflicts */}
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-1">
                  Cancelled Bookings ({auditResults.diagnosis_result.cancelled_bookings?.length || 0}):
                </h5>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {auditResults.diagnosis_result.cancelled_bookings?.map((b, i) => (
                    <div key={i} className={`p-2 rounded text-xs ${b.conflicts?.length > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                      <div className="font-medium">{b.other_student_name || b.user_id || 'Unknown'}</div>
                      <div className="text-gray-600">
                        Candidate: {b.other_student_candidate_no || 'N/A'} | 
                        User ID: {b.user_id || 'N/A'} |
                        Other Student: {b.is_other_student ? 'Yes' : 'No'}
                      </div>
                      {b.conflicts?.length > 0 && (
                        <div className="mt-1 text-red-700">
                          ⚠️ Conflicts: {b.conflicts.join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Recovery Result - Dry Run Preview */}
          {auditResults?.recovery_result?.dry_run && (
            <div className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-300">
              <h4 className="font-semibold text-yellow-800 mb-2">🔍 Restore Preview (DRY RUN)</h4>
              <p className="text-sm text-yellow-700 mb-3">এটা শুধু preview - কোন পরিবর্তন হয়নি। নিশ্চিত হলে Dry Run off করে আবার চালান।</p>
              
              <div className="grid grid-cols-2 gap-2 text-sm bg-white p-3 rounded mb-3">
                <div>Restore হবে: <strong className="text-green-600">{auditResults.recovery_result.bookings_restored || 0}</strong></div>
                <div>Skip হবে: <strong className="text-gray-600">{auditResults.recovery_result.skipped_bookings?.length || 0}</strong></div>
              </div>
              
              {auditResults.recovery_result.changes_preview?.length > 0 && (
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {auditResults.recovery_result.changes_preview.map((change, i) => (
                    <div key={i} className="p-2 bg-green-100 rounded text-sm">
                      <div className="font-medium">{change.user_name}</div>
                      <div className="text-xs text-gray-600">
                        {change.will_restore_lrw && <span className="mr-2">✓ LRW</span>}
                        {change.will_restore_speaking && <span>✓ Speaking</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {auditResults.recovery_result.skipped_bookings?.length > 0 && (
                <div className="mt-3">
                  <h5 className="text-sm font-medium text-gray-700 mb-1">Skip হবে:</h5>
                  <div className="space-y-1 max-h-[100px] overflow-y-auto">
                    {auditResults.recovery_result.skipped_bookings.map((skip, i) => (
                      <div key={i} className="p-1 bg-gray-100 rounded text-xs">
                        {skip.user_name}: {skip.reason}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Actual Restore Result (not dry run) */}
          {auditResults?.recovery_result && !auditResults.recovery_result.dry_run && (
            <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
              <h4 className="font-semibold text-blue-800 mb-2">♻️ Restore Result</h4>
              
              <div className="grid grid-cols-3 gap-2 text-sm bg-white p-3 rounded mb-3">
                <div>পাওয়া গেছে: <strong>{auditResults.recovery_result.found_cancelled || 0}</strong></div>
                <div>Restore হয়েছে: <strong className="text-green-600">{auditResults.recovery_result.bookings_restored || 0}</strong></div>
                <div>Skip হয়েছে: <strong className="text-orange-600">{auditResults.recovery_result.skipped_bookings?.length || 0}</strong></div>
              </div>
              
              {auditResults.recovery_result.message && (
                <p className="text-sm text-blue-700 mb-2">{auditResults.recovery_result.message}</p>
              )}
              
              {/* Restored bookings */}
              {auditResults.recovery_result.restored_bookings?.length > 0 && (
                <div className="mb-3">
                  <h5 className="text-sm font-medium text-green-700 mb-1">✅ Restored:</h5>
                  <div className="space-y-1 max-h-[150px] overflow-y-auto">
                    {auditResults.recovery_result.restored_bookings.map((b, i) => (
                      <div key={i} className="p-2 bg-green-100 rounded text-xs">
                        <span className="font-medium">{b.user_name}</span>
                        {b.restored_lrw && <span className="ml-2 text-green-700">✓ LRW</span>}
                        {b.restored_speaking && <span className="ml-2 text-green-700">✓ Speaking</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Skipped bookings with reasons */}
              {auditResults.recovery_result.skipped_bookings?.length > 0 && (
                <div className="mb-3">
                  <h5 className="text-sm font-medium text-orange-700 mb-1">⚠️ Skip হয়েছে (কারণ সহ):</h5>
                  <div className="space-y-1 max-h-[150px] overflow-y-auto">
                    {auditResults.recovery_result.skipped_bookings.map((skip, i) => (
                      <div key={i} className="p-2 bg-orange-100 rounded text-xs">
                        <span className="font-medium">{skip.user_name}</span>: 
                        <span className="text-orange-700 ml-1">{skip.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Errors */}
              {auditResults.recovery_result.errors?.length > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-red-700 mb-1">❌ Errors:</h5>
                  <div className="space-y-1">
                    {auditResults.recovery_result.errors.map((err, i) => (
                      <div key={i} className="p-2 bg-red-100 rounded text-xs text-red-700">{err}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {auditResults && !auditResults.recovery_result?.dry_run && (
            <div className="mt-4 p-4 bg-red-50 rounded-lg space-y-4 max-h-[500px] overflow-y-auto">
              {/* Summary */}
              <div>
                <h4 className="font-semibold text-lg">📊 Summary:</h4>
                {auditResults.summary && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm mt-2 bg-white p-3 rounded">
                    <div>Total Records: <strong>{auditResults.summary.total_booking_records}</strong></div>
                    <div>LRW Booked: <strong className="text-green-600">{auditResults.summary.lrw_booked}</strong></div>
                    <div>LRW Cancelled: <strong className="text-red-600">{auditResults.summary.lrw_cancelled}</strong></div>
                    <div>Speaking Booked: <strong className="text-green-600">{auditResults.summary.speaking_booked}</strong></div>
                    <div>Speaking Cancelled: <strong className="text-red-600">{auditResults.summary.speaking_cancelled}</strong></div>
                    <div>Total Slots: <strong>{auditResults.summary.total_speaking_slots}</strong></div>
                  </div>
                )}
              </div>
              
              {/* Issues */}
              {auditResults.summary?.issues_detected?.length > 0 && (
                <div className="bg-yellow-100 p-3 rounded border border-yellow-400">
                  <h5 className="font-semibold text-yellow-800">⚠️ Issues Detected:</h5>
                  <ul className="list-disc list-inside text-sm mt-1">
                    {auditResults.summary.issues_detected.map((issue, i) => (
                      <li key={i} className="text-yellow-800">{issue}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Bookings Missing Slot ID */}
              {auditResults.bookings_missing_slot_id?.length > 0 && (
                <div className="bg-orange-100 p-3 rounded">
                  <h5 className="font-semibold">🔧 Bookings with Missing speak_slot_id:</h5>
                  <div className="space-y-2 mt-2">
                    {auditResults.bookings_missing_slot_id.map((b, i) => (
                      <div key={i} className="bg-white p-2 rounded text-sm">
                        <div><strong>User:</strong> {b.user}</div>
                        <div><strong>Booking ID:</strong> {b.booking_id}</div>
                        <div><strong>Speaking Slot:</strong> {b.speaking_slot ? `${b.speaking_slot.date} ${b.speaking_slot.start}-${b.speaking_slot.end}` : 'N/A'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* All Bookings */}
              <div>
                <h4 className="font-semibold">📋 All Bookings ({auditResults.all_bookings?.length || 0}):</h4>
                <div className="space-y-2 mt-2">
                  {auditResults.all_bookings?.map((b, i) => (
                    <div key={i} className={`p-3 rounded text-sm ${b.lrw_status === 'CANCELLED' ? 'bg-red-100' : 'bg-white'}`}>
                      <div className="flex justify-between">
                        <strong>{b.user_info?.name || b.other_student_name || 'Unknown'}</strong>
                        <span className={b.lrw_status === 'BOOKED' ? 'text-green-600' : 'text-red-600'}>
                          {b.lrw_status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        <div>Email: {b.user_info?.email || 'N/A'}</div>
                        <div>LRW: {b.lrw_status} | Speaking: {b.speak_status || 'N/A'}</div>
                        <div>Speak Slot ID: {b.speak_slot_id || '❌ Missing'}</div>
                        {b.speaking_slot && (
                          <div>Speaking Time: {b.speaking_slot.date} {b.speaking_slot.start}-{b.speaking_slot.end}</div>
                        )}
                        <div>Booking ID: {b.id}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Debug Mock Speaking */}
      <Card className="border-2 border-purple-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            🔍 Debug Mock Speaking Slots
          </CardTitle>
          <CardDescription>
            কোন Mock এর Speaking slot count ভুল দেখালে Mock ID দিয়ে check করুন।
            এটা দেখাবে কেন count ভুল হচ্ছে।
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input 
              placeholder="Mock ID (যেমন: MK2025DEC13F)"
              value={debugMockId}
              onChange={(e) => setDebugMockId(e.target.value)}
              className="flex-1"
            />
            <Button 
              onClick={handleDebugMock}
              disabled={debugging}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {debugging ? '⏳ Checking...' : '🔍 Check করুন'}
            </Button>
          </div>
          
          {debugResults && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
              <h4 className="font-semibold">Analysis:</h4>
              {debugResults.analysis && (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Total Speaking Slots: <strong>{debugResults.analysis.total_speaking_slots}</strong></div>
                  <div>Slots marked booked: <strong>{debugResults.analysis.slots_with_is_booked_true}</strong></div>
                  <div>Actual bookings: <strong>{debugResults.analysis.actual_bookings_with_speaking}</strong></div>
                  <div>Occupancy total: <strong>{debugResults.analysis.occupancy_total_booked}</strong></div>
                  <div className={debugResults.analysis.mismatch_detected ? 'text-red-600 font-bold col-span-2' : 'text-green-600 col-span-2'}>
                    {debugResults.analysis.mismatch_detected ? '⚠️ MISMATCH DETECTED!' : '✅ No mismatch'}
                  </div>
                </div>
              )}
              
              {debugResults.bookings_with_speaking?.length > 0 && (
                <div className="mt-3">
                  <h5 className="font-medium">Bookings with Speaking ({debugResults.bookings_with_speaking.length}):</h5>
                  <ul className="text-xs mt-1 space-y-1">
                    {debugResults.bookings_with_speaking.map((b, i) => (
                      <li key={i} className="bg-white p-2 rounded">
                        User: {b.user_id || b.other_student_name || 'N/A'} | 
                        Slot ID: {b.speak_slot_id || 'N/A'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {debugResults.occupancy_records?.length > 0 && (
                <div className="mt-3">
                  <h5 className="font-medium">Occupancy Records:</h5>
                  <ul className="text-xs mt-1 space-y-1">
                    {debugResults.occupancy_records.map((o, i) => (
                      <li key={i} className="bg-white p-2 rounded">
                        Slot: {o.ref_id} | Booked: {o.booked_count}/{o.total_slots}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-gray-50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">ℹ️</span>
            <div className="text-sm text-gray-700">
              <p className="font-medium">কখন এগুলো ব্যবহার করবেন:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li><strong>Speaking Slot Sync:</strong> Mock card এ Speaking count ভুল দেখালে (যেমন LRW: 14/18 কিন্তু Speaking: 18/18)</li>
                <li><strong>Partial Duplicate Fix:</strong> একই student একই exam এ একাধিক বার book করে ফেললে</li>
                <li><strong>Debug Mock Speaking:</strong> কোন specific Mock এর Speaking সমস্যা investigate করতে</li>
              </ul>
              <p className="mt-2 text-gray-500">এই tools গুলো নিরাপদ - কোন data delete হবে না, শুধু সংশোধন হবে।</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* IP Blacklist Security Manager */}
      <div className="mt-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          🛡️ IP Security & Blacklist Management
        </h2>
        <IPBlacklistManager />
      </div>
    </div>
  );
};

export default SystemTools;
